import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePeersona } from '../../contexts/peersona-context';
import { useAutoGrowTextarea } from '../../hooks/utils/useAutoGrowTextarea';

/** Peersona input bar — rounded single-line shell, paper-airplane send icon. */
export const PeersonaInputBar: React.FC = () => {
  const { sendMessage, isSending, chatbot, panelOpen } = usePeersona();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(textareaRef, text, 4);

  // Keep focus on the input while sending. `disabled` drops focus to the page
  // behind the panel (KINDLY-1136); `readOnly` preserves it.
  useEffect(() => {
    if (panelOpen) {
      textareaRef.current?.focus();
    }
  }, [panelOpen]);

  const submit = useCallback(async () => {
    const t = text.trim();
    if (!t || isSending) return;
    setText('');
    await sendMessage(t);
  }, [text, isSending, sendMessage]);

  const peerLabel = chatbot?.display_name || chatbot?.name || 'Peersona';
  const placeholder = `Message ${peerLabel}…`;

  return (
    <div className="flex w-full shrink-0 flex-col pt-0.5">
      <div className="flex w-full items-center gap-1 rounded-[20px] border border-[rgba(144,161,185,0.4)] bg-white px-3 py-1.5">
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          readOnly={isSending}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          className="font-open-sans placeholder:text-muted-text-gray min-h-[24px] w-full flex-1 resize-none border-0 bg-transparent py-0 text-sm leading-6 tracking-[-0.266px] outline-none focus:ring-0"
          style={{ fontVariationSettings: "'wdth' 100" }}
        />
        <button
          type="button"
          aria-label="Send message"
          disabled={isSending || !text.trim()}
          onClick={() => void submit()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-indigo-500 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12 2-12 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};
